import { Injectable, inject } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { InactivityModalComponent } from "src/app-legacy/shared/components/inactivity-modal/inactivity-modal.component";
import { debounce } from "underscore";
import { INACTIVITY_PERIOD, LOGOUT_COUNTDOWN_PERIOD } from "../../../app-legacy/core/constants/constants";
import { AuthService } from "../../../app-legacy/core/services/auth.service";
import { StorageService } from "../../../app-legacy/core/services/storage.service";

@Injectable({
	providedIn: "root",
})
export class ActivityService {
	private matDialog = inject(MatDialog);
	private auth = inject(AuthService);
	private storage = inject(StorageService);

	/**
	 * List of events that will be used to check for user activity
	 */
	readonly ACTIVITY_EVENTS = ["click", "keydown", "mousemove", "wheel"];

	/**
	 * Debounced version of showInactivityModal
	 */
	debouncedShowInactivityModalFn = debounce(this.showInactivityModal, INACTIVITY_PERIOD);

	/**
	 * Function called when the user interacts with the page
	 */
	onActivity = this.reactToActivity.bind(this);

	/**
	 * Checks for user activity and resets the inactivity timer when the user interacts with the page.
	 * The timer is reset when the debounced function in `reactToActivity`  is called again before it is executed.
	 */
	checkForActivity(): void {
		const timeOfLastActivity = this.storage.getTimeOfLastActivity();

		if (timeOfLastActivity && Date.now() - timeOfLastActivity > INACTIVITY_PERIOD + LOGOUT_COUNTDOWN_PERIOD * 1000) {
			// Log out the user if they have already been inactive for longer than the allowed inactivity period
			this.auth.timeOutLogout();
			return;
		}

		this.ACTIVITY_EVENTS.forEach((event) => {
			document.addEventListener(event, this.onActivity, { passive: true });
		});

		window.addEventListener("storage", this.onActivity);
	}

	/**
	 * Tasks executed when the user interacts with the page.
	 * Shows the inactivity modal after the debounced period has passed and saves the time of the last activity.
	 */
	reactToActivity(event: Event): void {
		this.debouncedShowInactivityModalFn();

		if (event.type !== "storage") {
			// Ensure the event is not a storage event	to avoid recusively calling the function
			this.storage.saveTimeOfLastActivity();
		}
	}

	/**
	 * Removes event listeners that check for user activity
	 */
	stopCheckingForActivity(): void {
		this.ACTIVITY_EVENTS.forEach((event) => {
			document.removeEventListener(event, this.onActivity);
		});

		window.removeEventListener("storage", this.onActivity);

		this.debouncedShowInactivityModalFn.cancel();
	}

	/**
	 * Shows a modal that gives the user the option to stay logged in or log out automatically
	 */
	showInactivityModal(): void {
		this.stopCheckingForActivity();

		if (document.hidden) {
			const timeOfLastActivity = this.storage.getTimeOfLastActivity();

			if (timeOfLastActivity && Date.now() - timeOfLastActivity > INACTIVITY_PERIOD + LOGOUT_COUNTDOWN_PERIOD * 1000) {
				this.auth.timeOutLogout();
			}

			return;
		}

		this.matDialog.open(InactivityModalComponent, { width: "596px", disableClose: true });
	}
}
